import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getOrgs, getUnimportedEdlinkOrgEntries, importOrgFromEdlinkDistrictEntry } from '@/lib/api-calls';
import { edlinkIdSchema } from '@airreading/common/data';
import { Alert, AlertDescription } from '@/components/ui/alert';

const formSchema = z.object({
  districtId: edlinkIdSchema,
});

type ImportFromEdlinkProps = {
  trigger: React.ReactNode;
};

export const ImportFromEdlink = ({ trigger }: ImportFromEdlinkProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: importOrgFromEdlinkDistrictEntry,
  });

  const { data: viableEdlinkDistrictEntries } = useQuery(getUnimportedEdlinkOrgEntries());

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const entry = viableEdlinkDistrictEntries?.find((entry) => entry.districtId === formData.districtId);
    if (!entry) {
      toast({ title: `Selected unknown Edlink district ID ${formData.districtId}` });
      return;
    }
    return mutateAsync(entry)
      .then((newOrg) => {
        toast({
          title: 'Edlink district imported successfully',
        });
        queryClient.invalidateQueries(getOrgs());
        closeModal();
        navigate({ to: '/orgs/$orgId', params: { orgId: newOrg.id } });
      })
      .catch(() => {
        toast({
          title: 'Edlink district import failed',
        });
      });
  };

  return (
    <FormModal
      modalTitle="Import Edlink district"
      onSubmit={onSubmit}
      confirmBtnLabel="Import"
      submitButtonProps={{ disabled: !viableEdlinkDistrictEntries || !viableEdlinkDistrictEntries.length }}
      formSchema={formSchema}
      trigger={trigger}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid gap-4 py-4">
              {(() => {
                if (viableEdlinkDistrictEntries === undefined) {
                  return <></>;
                }
                if (viableEdlinkDistrictEntries === null) {
                  return (
                    <Alert variant="destructive">
                      <AlertDescription>Could not connect to Edlink API. Please contact engineering.</AlertDescription>
                    </Alert>
                  );
                }
                if (!viableEdlinkDistrictEntries.length) {
                  return (
                    <Alert variant="warning">
                      <AlertDescription>
                        Could not find any connected Edlink districts that are unimported.
                      </AlertDescription>
                    </Alert>
                  );
                }
                return (
                  <Alert variant="info">
                    <AlertDescription>The following unimported Edlink district IDs were found.</AlertDescription>
                  </Alert>
                );
              })()}
              {viableEdlinkDistrictEntries && viableEdlinkDistrictEntries.length ? (
                <FormSingleSelect
                  label="District Edlink ID"
                  options={viableEdlinkDistrictEntries.map((entry) => entry.districtId)}
                  form={form}
                  name="districtId"
                />
              ) : null}
            </div>
          </>
        );
      }}
    </FormModal>
  );
};
